import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { API_BASE_URL } from "../config";
import { useAppContext } from "../context/AppContext";
import { State, City } from "country-state-city";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import SeoHelmet from "./components/SeoHelmet";

const normalizeImage = (path) => {
  if (!path) return "/assets/img/a1-img/avatar-placeholder.png";
  if (typeof path !== "string") return "/assets/img/a1-img/avatar-placeholder.png";

  const cleanPath = path.trim();
  if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
    return cleanPath;
  }
  if (cleanPath.startsWith("//")) {
    return `https:${cleanPath}`;
  }
  if (
    cleanPath.startsWith("dsdxwhpuu/") ||
    cleanPath.startsWith("image/upload/") ||
    cleanPath.includes("cloudinary.com")
  ) {
    const withoutHost = cleanPath.replace(/^https?:\/\/res\.cloudinary\.com\/?/, "").replace(/^\/+/, "");
    return `https://res.cloudinary.com/${withoutHost}`;
  }
  if (cleanPath.startsWith("assets/") || cleanPath.startsWith("/assets/")) {
    return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  }

  const normalized = cleanPath.replace(/\\/g, "/").replace(/^\/+/, "");
  return `${API_BASE_URL}/${normalized}`;
};

export default function Profile() {
  const navigate = useNavigate();
  const { user: authUser, setUser: setGlobalUser, logout } = useAppContext();

  const [activeTab, setActiveTab] = useState("profile");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(!authUser);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editAddressId, setEditAddressId] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, type: "success", message: "" });
    }, 3000);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .trim()
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const [newAddress, setNewAddress] = useState({
    firstName: "",
    lastName: "",
    addressLine1: "",
    addressLine2: "",
    city: null,
    state: null,
    pincode: "",
    phone: "",
    country: "India",
  });

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const stateOptions = useMemo(
    () =>
      State.getStatesOfCountry("IN").map((s) => ({
        value: s.isoCode,
        label: s.name,
      })),
    [],
  );

  const cityOptions = useMemo(() => {
    if (!newAddress.state) return [];
    return City.getCitiesOfState("IN", newAddress.state.value).map((c) => ({
      value: c.name,
      label: c.name,
    }));
  }, [newAddress.state]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    fetchProfile();
  }, [token]);

  // Reset avatar error when authUser changes
  useEffect(() => {
    setAvatarError(false);
    if (authUser) setLoading(false);
  }, [authUser]);

  // Resolve scroll glitch/jump when switching between profile tabs
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });

    if (activeTab === "orders" && token) {
      fetchOrders();
    }
  }, [activeTab]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
        headers: { "x-auth-token": token },
      });
      if (setGlobalUser) setGlobalUser(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/orders/my-orders`, {
        headers: { "x-auth-token": token },
      });
      setOrders(res.data);
    } catch (err) {
      console.error("Error fetching orders", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleOpenModal = (address = null) => {
    if (address) {
      setIsEditing(true);
      setEditAddressId(address._id);

      const matchedState = stateOptions.find((s) => s.label === address.state);

      setNewAddress({
        firstName: address.firstName || "",
        lastName: address.lastName || "",
        addressLine1: address.addressLine1 || address.address || "",
        addressLine2: address.addressLine2 || address.apartment || "",
        pincode: address.pincode || "",
        phone: address.phone || "",
        state: matchedState || { value: "", label: address.state },
        city: address.city
          ? { value: address.city, label: address.city }
          : null,
        country: "India",
      });
    } else {
      setIsEditing(false);
      setEditAddressId(null);
      setNewAddress({
        firstName: "",
        lastName: "",
        addressLine1: "",
        addressLine2: "",
        city: null,
        state: null,
        pincode: "",
        phone: "",
        country: "India",
      });
    }

    setShowAddressModal(true);
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();

    const payload = {
      ...newAddress,
      state: newAddress.state?.label || "",
      city: newAddress.city?.label || "",
    };

    try {
      const url = isEditing
        ? `${API_BASE_URL}/api/auth/address/${editAddressId}`
        : `${API_BASE_URL}/api/auth/address`;

      const method = isEditing ? "put" : "post";

      await axios[method](url, payload, {
        headers: { "x-auth-token": token },
      });

      setShowAddressModal(false);
      showToast(
        isEditing
          ? "Address updated successfully"
          : "Address added successfully",
        "success",
      );
      fetchProfile();
    } catch (err) {
      showToast("Error saving address.", "danger");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this address?")) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/auth/address/${id}`, {
        headers: { "x-auth-token": token },
      });
      showToast("Address deleted", "success");
      fetchProfile();
    } catch (err) {
      showToast("Delete failed.", "danger");
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/auth/address/default/${id}`,
        {},
        { headers: { "x-auth-token": token } },
      );
      showToast("Primary address updated", "success");
      fetchProfile();
    } catch (err) {
      showToast("Failed to set primary address.", "danger");
    }
  };

  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || "processing";

    switch (s) {
      case "processing":
        return {
          bg: "bg-warning",
          text: "text-dark",
          icon: "fa-spinner fa-spin",
          label: "Processing",
        };
      case "shipped":
        return {
          bg: "bg-info",
          text: "text-white",
          icon: "fa-truck-fast",
          label: "In Transit",
        };
      case "delivered":
        return {
          bg: "bg-success",
          text: "text-white",
          icon: "fa-circle-check",
          label: "Delivered",
        };
      case "cancelled":
        return {
          bg: "bg-danger",
          text: "text-white",
          icon: "fa-circle-xmark",
          label: "Cancelled",
        };
      default:
        return {
          bg: "bg-secondary",
          text: "text-white",
          icon: "fa-box",
          label: status || "Unknown",
        };
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      <SeoHelmet
        title="My Account | A1 Bakery & Sweets"
        description="Manage your profile, saved addresses, and track your recent sweet orders at A1 Bakery & Sweets."
        canonical={window.location.origin + "/profile"}
      />
      <Navbar />

      <div className="container mt-100 mb-100 flex-grow-1">
        <div className="breadcrumb__list mb-30">
          <span>
            <Link to="/">Home</Link>
          </span>
          <span className="dvdr">
            <i className="fa-regular fa-angle-right"></i>
          </span>
          <span>My Profile</span>
        </div>
        {loading && !authUser ? (
          <div className="text-center py-5 my-5">
            <div className="spinner-border text-danger"></div>
            <p className="mt-3 text-muted">Loading your profile...</p>
          </div>
        ) : (
          <div className="row">
            {/* Sidebar */}
            <div className="col-md-4 mb-4">
              <div
                className="card shadow-sm border-0 p-4 rounded-4 text-center sticky-md-top"
                style={{ top: "110px", zIndex: 10 }}
              >
                <div className="d-flex justify-content-center">
                  {(authUser?.avatar || authUser?.img) && !avatarError ? (
                    <img
                      src={normalizeImage(authUser?.avatar || authUser?.img)}
                      className="rounded-circle shadow border p-1 mb-3"
                      onError={() => setAvatarError(true)}
                      width="100"
                      height="100"
                      alt="avatar"
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-danger text-white d-flex align-items-center justify-content-center mb-3 shadow mx-auto"
                      style={{
                        width: "100px",
                        height: "100px",
                        fontSize: "32px",
                        fontWeight: "bold",
                      }}
                    >
                      {getInitials(authUser?.name)}
                    </div>
                  )}
                </div>

                <h4 className="fw-bold mb-1 text-dark">{authUser?.name}</h4>
                <p className="text-muted small mb-4">{authUser?.email}</p>

                <hr />

                <div className="list-group list-group-flush text-start">
                  <button
                    className={`list-group-item list-group-item-action border-0 rounded-3 mb-2 ${
                      activeTab === "profile"
                        ? "active bg-danger text-white"
                        : ""
                    }`}
                    onClick={() => setActiveTab("profile")}
                  >
                    <i className="fal fa-map-marker-alt me-2"></i> Saved
                    Addresses
                  </button>

                  <button
                    className={`list-group-item list-group-item-action border-0 rounded-3 ${
                      activeTab === "orders"
                        ? "active bg-danger text-white"
                        : ""
                    }`}
                    onClick={() => setActiveTab("orders")}
                  >
                    <i className="fal fa-shopping-bag me-2"></i> My Orders
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="col-md-8">
              <div className="card shadow-sm border-0 p-4 p-md-5 rounded-4 bg-white">
                {activeTab === "profile" ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-5">
                      <h3 className="fw-bold m-0">My Addresses</h3>
                      <button
                        className="btn btn-danger btn-sm px-4 fw-bold"
                        onClick={() => handleOpenModal()}
                      >
                        + Add New
                      </button>
                    </div>

                    <div className="row">
                      {authUser?.addresses?.length > 0 ? (
                        authUser.addresses.map((addr) => (
                          <div key={addr._id} className="col-md-6 mb-3">
                            <div
                              className={`border p-3 rounded-4 shadow-sm h-100 position-relative ${
                                addr.isDefault
                                  ? "border-danger bg-light"
                                  : "bg-white"
                              }`}
                            >
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <span
                                  className={`badge ${
                                    addr.isDefault
                                      ? "bg-danger"
                                      : "bg-secondary"
                                  }`}
                                >
                                  {addr.isDefault ? "Primary" : "Additional"}
                                </span>

                                <div className="dropdown">
                                  <button
                                    className="btn btn-sm p-0"
                                    data-bs-toggle="dropdown"
                                    type="button"
                                  >
                                    <i className="fas fa-ellipsis-v text-muted"></i>
                                  </button>

                                  <ul className="dropdown-menu dropdown-menu-end shadow border-0">
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleOpenModal(addr)}
                                      >
                                        Edit
                                      </button>
                                    </li>

                                    {!addr.isDefault && (
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() =>
                                            handleSetPrimary(addr._id)
                                          }
                                        >
                                          Set as Primary
                                        </button>
                                      </li>
                                    )}

                                    <li>
                                      <hr className="dropdown-divider" />
                                    </li>

                                    <li>
                                      <button
                                        className="dropdown-item text-danger"
                                        onClick={() => handleDelete(addr._id)}
                                      >
                                        Delete
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </div>

                              <p className="m-0 fw-bold">
                                {addr.firstName} {addr.lastName}
                              </p>
                              <p className="m-0 small text-muted">
                                {addr.addressLine1 || addr.address}
                              </p>

                              {(addr.addressLine2 || addr.apartment) && (
                                <p className="m-0 small text-muted">
                                  {addr.addressLine2 || addr.apartment}
                                </p>
                              )}

                              <p className="m-0 small text-muted">
                                {addr.city}, {addr.state} - {addr.pincode}
                              </p>

                              <p className="m-0 small fw-bold mt-2">
                                Ph: {addr.phone}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-12">
                          <div className="text-center py-5">
                            <i className="fal fa-map-marker-alt fa-3x text-muted mb-3"></i>
                            <p className="text-muted mb-0">
                              No saved addresses found.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-5">
                      <h3 className="fw-bold mb-1">Your Journey with A1</h3>
                      <p className="text-muted small">
                        Relive your sweet moments and track recent delights.
                      </p>
                    </div>

                    {ordersLoading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-danger"></div>
                      </div>
                    ) : orders.length > 0 ? (
                      <div className="d-flex flex-column gap-4">
                        {orders.map((order) => {
                          const status = getStatusConfig(order.orderStatus);

                          return (
                            <div
                              key={order._id}
                              className="card border rounded-4 overflow-hidden shadow-none hover-shadow transition-all"
                            >
                              <div className="card-header bg-light border-0 py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                  <span className="text-muted small text-uppercase fw-bold ls-1">
                                    Order Ref:
                                  </span>{" "}
                                  <span className="fw-bold text-dark">
                                    #{order._id.slice(-6).toUpperCase()}
                                  </span>
                                </div>

                                <div className="small text-muted">
                                  Placed on{" "}
                                  {new Date(order.createdAt).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    },
                                  )}
                                </div>
                              </div>

                              <div className="card-body p-4">
                                <div className="row align-items-center">
                                  <div className="col-md-7 mb-3 mb-md-0">
                                    <div className="d-flex gap-2 mb-3 flex-wrap">
                                      {order.items
                                        ?.slice(0, 3)
                                        .map((item, i) => (
                                          <img
                                            key={i}
                                            src={normalizeImage(item.image || item.img)}
                                            className="rounded-3 border object-fit-cover"
                                            style={{
                                              width: "145px",
                                              height: "100px",
                                            }}
                                            alt={item.name || "item"}
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.src = "/assets/img/a1-img/placeholder.png";
                                            }}
                                          />
                                        ))}

                                      {order.items?.length > 3 && (
                                        <div
                                          className="rounded-3 border bg-light d-flex align-items-center justify-content-center small fw-bold"
                                          style={{
                                            width: "50px",
                                            height: "50px",
                                          }}
                                        >
                                          +{order.items.length - 3}
                                        </div>
                                      )}
                                    </div>

                                    <div className="medium mb-0">
                                      {order.items
                                        ?.map((i) => i.name)
                                        .join(", ")
                                        .substring(0, 60)}
                                    </div>
                                  </div>

                                  <div className="col-md-5 text-md-end">
                                    <span
                                      className={`badge ${status.bg} ${status.text} px-3 py-2 rounded-pill mb-3`}
                                    >
                                      <i
                                        className={`fas ${status.icon} me-2`}
                                      ></i>
                                      {status.label}
                                    </span>

                                    {order.orderStatus !== "Abandoned" && (
                                      <>
                                        <br />
                                        <button
                                          onClick={() =>
                                            navigate(
                                              `/order-success/${order._id}`,
                                            )
                                          }
                                          className="btn btn-outline-dark btn-sm rounded-pill px-4 fw-bold"
                                        >
                                          Details
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <i className="fal fa-box-open fa-3x text-muted mb-3"></i>
                        <p className="text-muted">
                          You haven't placed any orders yet.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Address Modal */}
      {showAddressModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 p-4 pb-0">
                <h4 className="modal-title fw-bold">
                  {isEditing ? "Edit Address" : "Add Address"}
                </h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddressModal(false)}
                ></button>
              </div>

              <form onSubmit={handleSaveAddress}>
                <div className="modal-body px-4 pb-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">
                        First Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        placeholder="Enter First Name"
                        value={newAddress.firstName}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            firstName: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold">
                        Last Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        placeholder="Enter Last Name"
                        value={newAddress.lastName}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            lastName: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label small fw-bold">
                        Street Address / House No.
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        placeholder="House No, Building Name"
                        value={newAddress.addressLine1}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            addressLine1: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label small fw-bold">
                        Apartment / Landmark
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Apartment, Landmark etc."
                        value={newAddress.addressLine2}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            addressLine2: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold">State</label>
                      <Select
                        options={stateOptions}
                        value={newAddress.state}
                        placeholder="Search State..."
                        onChange={(opt) =>
                          setNewAddress({
                            ...newAddress,
                            state: opt,
                            city: null,
                          })
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold">City</label>
                      <Select
                        options={cityOptions}
                        value={newAddress.city}
                        placeholder="Search City..."
                        isDisabled={!newAddress.state}
                        onChange={(opt) =>
                          setNewAddress({ ...newAddress, city: opt })
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold">
                        Pincode
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        value={newAddress.pincode}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            pincode: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Phone</label>
                      <PhoneInput
                        country={"in"}
                        value={newAddress.phone}
                        containerClass="w-100"
                        inputStyle={{ width: "100%", height: "38px" }}
                        onChange={(phone) =>
                          setNewAddress({ ...newAddress, phone })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer border-0 p-4 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={() => setShowAddressModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-danger px-5 fw-bold">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <div
          className={`position-fixed top-0 end-0 mt-5 me-3 p-3 text-white rounded shadow-lg ${
            toast.type === "success" ? "bg-success" : "bg-danger"
          }`}
          style={{ zIndex: 1060 }}
        >
          {toast.message}
        </div>
      )}

      <Footer />
    </div>
  );
}
